// src/components/ContabilitaLeMieOre.js
// ═══════════════════════════════════════════════════════════════════════════
// «LE MIE ORE» NEL CONTROLLO ECONOMICO — la contabilità a parte (ottobre 2026, W412)
// ═══════════════════════════════════════════════════════════════════════════
// Riquadro del Controllo Economico della commessa con il dettaglio delle ore segnate in
// «Le mie ore»: persona per persona, giorno per giorno, blocco per blocco, con il costo.
//  • contano (e sono già nel totale Manodopera, non si sommano di nuovo) le ore del personale
//    Non operativo con account collegato alla scheda, al suo costo orario (W434);
//  • ore di chi non è (più) Non operativo con scheda collegata: elencate a parte, per
//    informazione;
//  • un tempo rimasto acceso da un giorno precedente non conta finché non viene chiuso;
//  • esportazione in Excel di tutti i blocchi.
// Calcoli in utils/diarioOre.js (contabilitaDiario), gli stessi del totale Manodopera.
// ═══════════════════════════════════════════════════════════════════════════
import React, { useEffect, useMemo, useState } from "react";
import { contabilitaDiario, durata, oraDi } from "../utils/diarioOre";
import { esportaXLSX } from "../utils/esportaXLSX";

const VIOLA = "#7c3aed";
const euro = (n) => `€ ${(Math.round((n || 0) * 100) / 100).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const dataIt = (iso) => (iso ? new Date(iso + "T00:00:00").toLocaleDateString("it-IT", { weekday: "short", day: "2-digit", month: "2-digit", year: "numeric" }) : "—");
const MESI = ["gennaio", "febbraio", "marzo", "aprile", "maggio", "giugno", "luglio", "agosto", "settembre", "ottobre", "novembre", "dicembre"];
const meseIt = (k) => (k ? `${MESI[Number(k.slice(5, 7)) - 1]} ${k.slice(0, 4)}` : "—");
const quando = (b) => (b.inizio ? `${oraDi(b.inizio)}–${b.inCorso ? "in corso" : oraDi(b.fine)}` : "a mano");

export default function ContabilitaLeMieOre({ blocchi = [], dipendenti = [], uidContati, numIt, codiceCommessa = "", nomeCommessa = "", onNavigate, puoAprireDipendenti = false }) {
  const [adesso, setAdesso] = useState(Date.now());
  const [aperto, setAperto] = useState(true);
  const [persona, setPersona] = useState(null);
  const conta = useMemo(() => contabilitaDiario(blocchi, dipendenti, uidContati || new Set(), adesso, numIt), [blocchi, dipendenti, uidContati, adesso, numIt]);
  useEffect(() => {
    if (!conta.inCorso) return undefined;
    const t = setInterval(() => setAdesso(Date.now()), 60000); // il tempo in corso cresce minuto per minuto
    return () => clearInterval(t);
  }, [conta.inCorso]);

  if (!conta.persone.length && !conta.fuori.length && !conta.dimenticati.length) return null;

  const esporta = () => {
    const righe = [["Persona", "Data", "Dalle–alle", "Minuti", "Ore", "Costo orario €", "Costo €", "Nota", "Contabilizzata"]];
    conta.persone.forEach(p => p.giorni.slice().reverse().forEach(g => g.blocchi.forEach(b => righe.push([
      p.nome, b.data, quando(b), b.minutiCalcolati, Math.round(b.minutiCalcolati / 60 * 100) / 100, p.costoOrario,
      Math.round(b.minutiCalcolati / 60 * p.costoOrario * 100) / 100, b.nota || "", "sì"]))));
    conta.fuori.forEach(f => righe.push([f.nome, "", "", f.minuti, Math.round(f.minuti / 60 * 100) / 100, "", "", "fonte ore dell'azienda", "no"]));
    esportaXLSX(righe, `LeMieOre_${codiceCommessa || nomeCommessa || "commessa"}_${new Date().toLocaleDateString("it-IT").replace(/\//g, "-")}`, "Le mie ore");
  };

  const td = { padding: "6px 10px", borderBottom: "1px solid #ede9fe", fontSize: 12.5 };
  return (
    <div role="region" aria-label="Le mie ore — contabilità" style={{ background: "#fff", borderLeft: `1.5px solid ${VIOLA}55`, borderRight: `1.5px solid ${VIOLA}55`, borderBottom: `1.5px solid ${VIOLA}55`, borderTop: `4px solid ${VIOLA}`, borderRadius: 16, marginBottom: 24, boxShadow: "0 4px 14px rgba(17,24,39,.07)", overflow: "hidden" }}>
      <button type="button" onClick={() => setAperto(a => !a)} aria-expanded={aperto}
        style={{ width: "100%", display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", padding: "14px 20px", background: `${VIOLA}0d`, border: "none", cursor: "pointer", textAlign: "left", fontFamily: "'Inter',sans-serif" }}>
        <span style={{ fontSize: 14.5, fontWeight: 800, color: VIOLA, flex: 1, minWidth: 200 }}>⏱️ Le mie ore — contabilità</span>
        <span style={{ fontSize: 12.5, color: "#6b7280" }}>{durata(conta.minuti)}{conta.inCorso ? " · tempo in corso" : ""}</span>
        <span style={{ fontSize: 15, fontWeight: 800, color: "#111827" }}>{euro(conta.totale)}</span>
        <span style={{ color: "#9ca3af" }}>{aperto ? "▲" : "▼"}</span>
      </button>
      {aperto && (
        <div style={{ padding: "12px 20px 18px" }}>
          <p style={{ fontSize: 11.5, color: "#6b7280", margin: "0 0 10px", lineHeight: 1.55 }}>
            Le ore segnate in «Le mie ore» su questa commessa. Sono le ore del personale Non operativo, al costo orario della sua scheda in <b>Dipendenti</b>:
            sono <b>già comprese nel totale Manodopera</b> (righe «⏱️ Le mie ore»), qui c'è il dettaglio.
          </p>

          {conta.dimenticati.map(b => (
            <div key={b.id || b.inizio} style={{ background: "#fef2f2", border: "1px solid #fecaca", borderLeft: "3px solid #dc2626", borderRadius: 8, padding: "8px 12px", marginBottom: 8, fontSize: 12, color: "#991b1b" }}>
              ⚠️ <b>{conta.persone.find(p => p.uid === b.uid)?.nome || b.nomeUtente || "Un collega"}</b> ha il tempo rimasto acceso dal {dataIt(b.data)} (dalle {oraDi(b.inizio)}): <b>non è contato</b> finché in «Le mie ore» non scrive a che ora ha finito.
            </div>
          ))}
          {conta.persone.filter(p => p.senzaCosto).map(p => (
            <div key={"sc" + p.uid} style={{ background: "#fffbeb", border: "1px solid #fde68a", borderLeft: "3px solid #f59e0b", borderRadius: 8, padding: "8px 12px", marginBottom: 8, fontSize: 12, color: "#92400e", display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              <span style={{ flex: 1 }}>⚠️ <b>{p.nome}</b>: {durata(p.minuti)} senza costo orario, quindi a € 0. Inserisci il costo orario in Dipendenti.</span>
              {puoAprireDipendenti && onNavigate && <button type="button" onClick={() => onNavigate({ page: "dipendenti" })} style={{ fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 6, border: "1px solid #f59e0b", background: "#fff", color: "#92400e", cursor: "pointer" }}>Dipendenti →</button>}
            </div>
          ))}

          {conta.mesi.length > 1 && (
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
              {conta.mesi.map(m => (
                <span key={m.mese} style={{ fontSize: 11.5, padding: "3px 10px", borderRadius: 999, background: "#f5f3ff", color: "#5b21b6", fontWeight: 600 }}>
                  {meseIt(m.mese)}: {durata(m.minuti)} · {euro(m.totale)}
                </span>
              ))}
            </div>
          )}

          {conta.persone.length > 0 ? (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 420 }}>
                <thead><tr style={{ background: "#f5f3ff" }}>
                  {["Persona", "Ore", "Costo/h", "Totale"].map((h, i) => <th key={h} style={{ ...td, fontSize: 11, fontWeight: 700, color: "#5b21b6", textAlign: i ? "right" : "left" }}>{h}</th>)}
                </tr></thead>
                <tbody>
                  {conta.persone.map(p => (
                    <React.Fragment key={p.uid}>
                      <tr onClick={() => setPersona(x => (x === p.uid ? null : p.uid))} style={{ cursor: "pointer" }} title="Mostra giorni e orari">
                        <td style={{ ...td, fontWeight: 600, color: "#111827" }}>{persona === p.uid ? "▾" : "▸"} {p.nome}</td>
                        <td style={{ ...td, textAlign: "right" }}>{durata(p.minuti)}</td>
                        <td style={{ ...td, textAlign: "right", color: "#6b7280" }}>{p.costoOrario > 0 ? euro(p.costoOrario) : "—"}</td>
                        <td style={{ ...td, textAlign: "right", fontWeight: 700, color: VIOLA }}>{euro(p.totale)}</td>
                      </tr>
                      {persona === p.uid && p.giorni.map(g => (
                        <tr key={p.uid + g.data}>
                          <td colSpan={4} style={{ ...td, background: "#faf9ff", paddingLeft: 26 }}>
                            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", fontSize: 12 }}>
                              <b style={{ minWidth: 120, color: "#374151" }}>{dataIt(g.data)}</b>
                              <span style={{ color: "#6b7280" }}>{durata(g.minuti)} · {euro((g.minuti / 60) * (p.costoOrario || 0))}</span>
                            </div>
                            {g.blocchi.map((b, i) => (
                              <div key={b.id || i} style={{ fontSize: 11.5, color: "#6b7280", marginTop: 2 }}>
                                {quando(b)} · {durata(b.minutiCalcolati)}{b.nota ? ` · ${b.nota}` : ""}
                              </div>
                            ))}
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                  <tr>
                    <td style={{ ...td, fontWeight: 800 }}>Totale</td>
                    <td style={{ ...td, textAlign: "right", fontWeight: 800 }}>{durata(conta.minuti)}</td>
                    <td style={td} />
                    <td style={{ ...td, textAlign: "right", fontWeight: 800, color: VIOLA }}>{euro(conta.totale)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ fontSize: 12.5, color: "#6b7280" }}>Nessuna ora contabilizzata da «Le mie ore» su questa commessa.</div>
          )}

          {conta.fuori.length > 0 && (
            <div style={{ marginTop: 12, fontSize: 11.5, color: "#6b7280", background: "#f9fafb", borderRadius: 8, padding: "8px 12px", lineHeight: 1.55 }}>
              <b>Segnate in «Le mie ore» ma non contate qui</b> (in Dipendenti non risultano Non operativi con l'account collegato: per i costi vale la fonte ore dell'azienda): {conta.fuori.map(f => `${f.nome} ${durata(f.minuti)}`).join(" · ")}.
            </div>
          )}

          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
            <button type="button" onClick={esporta} style={{ display: "flex", alignItems: "center", gap: 5, padding: "6px 12px", border: "1px solid #059669", borderRadius: 7, background: "#f0fdf4", color: "#059669", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "'Inter',sans-serif" }}>
              📊 Excel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
